import {Storage} from "@ionic/storage-angular";
import { Injectable } from "@angular/core";
import { UserInfo } from "~/app/core/model/user-info";
import { OfferInfo } from "~/app/core/model/offer-info";
import { OfferSearch } from "~/app/core/model/offer-search";
import { InitInfo } from "~/app/core/model/init-info";
import { Settings } from "~/app/core/model/settings";
import { momentNow, toDate, toDateStr } from "~/app/core/model/date-str";
import { NotificationInfo } from "~/app/core/model/notification-info";
import { LocationInfoRPC } from "~/app/core/model/rpc/location-info-rpc";
import * as Log from "~/app/core/log";
import * as _ from "lodash";
import { RPCRequest } from "~/app/core/model/rpc/rpc-request";
import { SponsorInfo } from "~/app/core/model/sponsor-info";
import { InteractionAggregate } from "~/app/core/model/interaction/interaction-aggregate";
import { OfferSearchResultLocation } from "~/app/core/model/offer-search-result-location";
import { toPlainString } from "~/app/core/model/formatted-string";
import { TicketInfo } from "~/app/core/model/ticket-info";
import { v4 as uuid } from 'uuid';
import {DataStorageService} from "~/app/core/data-storage.service";
import {Observable} from "rxjs";

@Injectable()
export class StorageService {
    private readonly KEY_VERSION: string = "Version";
    private readonly KEY_INIT: string = "Init";
    private readonly KEY_DEVICE: string = "Device";
    private readonly KEY_SETTINGS: string = "Settings";
    private readonly KEY_INTERACTION: string = "Interaction";
    private readonly KEY_RPC_QUEUE: string = "RPCQueue";
    private readonly KEY_USERSESSION: string = "UserSession";
    private readonly KEY_TICKET_COUNT: string = "TicketCount";
    private readonly KEY_TICKETS_ACTUAL: string = "TicketsActual";
    private readonly KEY_SPONSOR_INFO: string = "SponsorInfo";
    private readonly KEY_NOTIFICATIONS: string = "Notifications";
    private readonly KEY_NOTIFICATION_TOPICS: string = "NotificationTopics";
    private readonly KEY_USER_LOCATION: string = "UserLocation";
    private readonly KEY_OFFER_SEARCH_RESULT_LOCATION: string = "OfferSearchResultLocation";
    private readonly KEY_OFFER_SEARCH: string = "OfferSearch";
    private readonly KEY_OFFER_SEARCH_DATE: string = "OfferSearchDate";
    private readonly KEY_OFFER_SEARCH_EXPIRES_DATE: string = "OfferSearchExpiresDate";
    private readonly KEY_OFFER_PREFIX: string = "Offer_";
    private readonly KEY_OFFER_LIST_ALL: string = "OfferListAll";
    private readonly KEY_OFFER_LIST_SEARCH: string = "OfferListSearch";
    private readonly KEY_OFFER_LIST_REDEEMED: string = "OfferListRedeemed";
    private readonly KEY_SHARE_DATE: string = "ShareDate";
    private readonly KEY_REFERRAL_CODE: string = "ReferralCode";

    constructor(
        private readonly data: DataStorageService,
    ) {
        // nothing
        this.data.waitForReady().then(() => {
            this.presetData();
            this.updateVersion();
        });
        // If storage service gets instantiated in multiple places, this breaks things.
        // It doesn't seem necessary to reset the data, so I'm commenting it out until I can figure out how to prevent multiple instance of DataStorageService
        // this.resetData();
    }

    waitForReady(): Promise<void> {
        return this.data.waitForReady();
    }

    resetData(): void {
        this.storeOfferSearchDate(null);
        this.storeOfferSearch(null);
        this.storeSponsorInfo(null);
        this.storeInteraction(null);
        this.storeNotifications(null);
        this.resetOffers();
    }

    resetLogin(): void {
        this.storeUserInfo(null, this.loadSettings().env);
    }

    resetOffers(): void {
        const allOffers: {[key: string]: boolean} = this.data.load(this.KEY_OFFER_LIST_ALL);
        if (allOffers) {
            Object.keys(allOffers).forEach((key) => {
                this.removeOfferInfo(Number(key));
            });
        }
        this.data.store(this.KEY_OFFER_LIST_ALL, {});
        this.storeOfferListRedeemed(null);
        this.storeOfferListSearch(null);
    }

    // VERSION DATA
    updateVersion(): number {
        const existingVersionCode: number = this.data.load(this.KEY_VERSION);
        let versionCode = null;
        Log.info(Log.Tag.StorageService, "App version is " + versionCode + ".");

        if (existingVersionCode !== versionCode) {
            Log.info(Log.Tag.StorageService, "App version changed from " + existingVersionCode + " to " + versionCode + ".");
            // the app version has been updated, so do some data cleanup to avoid potential glitches?
            if (existingVersionCode <= 2001091457) {
                this.storeSponsorInfo(null);
                this.resetOffers();
            }
            if (existingVersionCode <= 2002061744) {
                this.resetData();
                // time to upgrade to production mode for the app
                const settings = new Settings();
                settings.preconfigureSettings("production");
                this.storeSettings(settings);
            }
            if (existingVersionCode <= 2009000000) {
                // add "purchase survey dates" to all redeemed offers
                const offerList = this.loadOffersListSearch();
                if (offerList) {
                    for (const offerId of offerList) {
                        const offer = this.loadOfferInfo(offerId);
                        if (offer.redeemedDate) {
                            offer.purchaseSurveyAnswer = "yes";
                            offer.purchaseSurveyDate = toDateStr(new Date());
                            this.storeOfferInfo(offer);
                        }
                    }
                }
            }

            this.data.store(this.KEY_VERSION, versionCode);
        }

        return versionCode;
    }

    loadVersion(): number {
        let version = this.data.load(this.KEY_VERSION);
        if (version === null) {
            version = this.updateVersion();
        }
        return version;
    }

    // SETTINGS DATA
    storeSettings(settings: Settings): void {
        this.data.store(this.KEY_SETTINGS, settings);
    }

    loadSettings(): Settings {
        return this.data.load(this.KEY_SETTINGS);
    }

    // INTERACTION DATA
    storeInteraction(aggregate: InteractionAggregate): void {
        this.data.store(this.KEY_INTERACTION, aggregate);
    }

    loadInteraction(): InteractionAggregate {
        let aggregate: InteractionAggregate = this.data.load(this.KEY_INTERACTION);
        if (!aggregate) {
            aggregate = new InteractionAggregate();
            const wifiReportingInterval = (60 * 1000);
            const offlineReportingInterval = (60 * 60 * 1000);
            aggregate.deadline = toDateStr(new Date(new Date().getTime() + offlineReportingInterval));
            aggregate.wifiDeadline = toDateStr(new Date(new Date().getTime() + wifiReportingInterval));
        }
        return _.cloneDeep(aggregate);
    }

    // INIT DATA
    storeInitInfo(initInfo: InitInfo): void {
        this.data.store(this.KEY_INIT, initInfo);
    }

    loadInitInfo(): InitInfo {
        return _.cloneDeep(this.data.load(this.KEY_INIT));
    }

    // DEVICE DATA
    storeDevice(device: string): void {
        this.data.store(this.KEY_DEVICE, device);
    }

    loadDevice(): string {
        let device = this.data.load(this.KEY_DEVICE);
        if (!device) {
            device = uuid();
            this.data.store(this.KEY_DEVICE, device);
        }
        return device;
    }

    // USER DATA
    storeUserInfo(userInfo: UserInfo, env: string): void {
      this.data.store(this.KEY_USERSESSION + this.userDataSuffix(env), userInfo);
    }

    loadUserInfo(env: string): UserInfo {
      return _.cloneDeep(this.data.load(this.KEY_USERSESSION + this.userDataSuffix(env)));
    }

    // REFERRAL CODE
  storeCode(referralCode: string): void {
    this.data.store(this.KEY_REFERRAL_CODE, referralCode);
  }

  loadCode(): string {
    return _.cloneDeep(this.data.load(this.KEY_REFERRAL_CODE));
  }

    // TICKET DATA
    storeTicketInfoActual(ticketInfo: TicketInfo, env: string): void {
        this.data.store(this.KEY_TICKETS_ACTUAL + this.userDataSuffix(env), ticketInfo);
    }

    loadTicketInfoActual(env: string): TicketInfo {
        let tickets = _.cloneDeep(this.data.load(this.KEY_TICKETS_ACTUAL + this.userDataSuffix(env)));
        if (!tickets) {
            tickets = { tickets: 10 };
            this.storeTicketInfoActual(tickets, env);
        }

        if (!tickets.dailyTasks || !tickets.dailyTasks.date) {
            tickets.dailyTasks = {
                date: toDateStr(momentNow())
            };
            this.storeTicketInfoActual(tickets, env);
        }
        return tickets;
    }

    storeTicketCount(ticketCount: number, env: string): void {
        this.data.store(this.KEY_TICKET_COUNT + this.userDataSuffix(env), ticketCount);
    }

    loadTicketCount(env: string): number {
        let tickets = _.cloneDeep(this.data.load(this.KEY_TICKET_COUNT + this.userDataSuffix(env)));
        if (!tickets) {
            tickets = 0;
            this.storeTicketCount(tickets, env);
        }
        return tickets;
    }

    // SPONSOR INFO
    storeSponsorInfo(sponsor: SponsorInfo) {
        this.data.store(this.KEY_SPONSOR_INFO, sponsor);
    }

    loadSponsorInfo(): SponsorInfo {
        return _.cloneDeep(this.data.load(this.KEY_SPONSOR_INFO));
    }

    // OFFER INFO
    storeOfferInfo(offer: OfferInfo) {
        this.data.store(this.KEY_OFFER_PREFIX + offer.id, offer);
        this.updateOfferTracker(offer.id, true);
    }

    loadOfferInfo(id: number): OfferInfo {
        const offerInfo = this.data.load(this.KEY_OFFER_PREFIX + id)
        if (offerInfo) {
            const o = OfferInfo.migrate(_.cloneDeep(offerInfo));

            o.filterText = " ";
            if (o.name) o.filterText += o.name.toLowerCase() + " ";
            if (o.terms) o.filterText += o.terms.toLowerCase() + " ";
            if (o.additionalTerms) o.filterText += o.additionalTerms.toLowerCase() + " ";
            if (o.companyName) o.filterText += o.companyName.toLowerCase() + " ";
            if (o.companyDescription) o.filterText += toPlainString(o.companyDescription).toLowerCase() + " ";
            if (o.whatElseDescription && o.whatElseDescription.description) o.filterText += toPlainString(o.whatElseDescription.description).toLowerCase() + " ";
            if (o.addresses) {
                o.addresses.forEach((a) => {
                    if (a.city) o.filterText += a.city.toLowerCase() + " ";
                    if (a.phone) o.filterText += a.phone.toLowerCase() + " ";
                    if (a.state) o.filterText += a.state.toLowerCase() + " ";
                    if (a.street1) o.filterText += a.street1.toLowerCase() + " ";
                    if (a.street2) o.filterText += a.street2.toLowerCase() + " ";
                    if (a.zip) o.filterText += a.zip.toLowerCase() + " ";
                });
            }

            return o;
        } else {
            return null;
        }
    }

    removeOfferInfo(id: number) {
        this.data.store(this.KEY_OFFER_PREFIX + id, null);
        this.updateOfferTracker(id, false);
    }

    // OFFER LIST SEARCH
    storeOfferListSearch(offers: Array<number>) {
        this.data.store(this.KEY_OFFER_LIST_SEARCH, offers);
    }

    loadOffersListSearch(): Array<number> {
        return _.cloneDeep(this.data.load(this.KEY_OFFER_LIST_SEARCH));
    }

    // OFFER LIST REDEEMED
    storeOfferListRedeemed(offers: Array<number>) {
        this.data.store(this.KEY_OFFER_LIST_REDEEMED, offers);
    }

    loadOffersListRedeemed(): Array<number> {
        const offers = this.data.load(this.KEY_OFFER_LIST_REDEEMED);
        return offers ? _.cloneDeep(offers) : [];
    }

    addOffersListRedeemedOffer(offerId: number) {
        // add id to redeemed offer list
        let offerList: Array<number> = this.loadOffersListRedeemed();
        if (!offerList) {
            offerList = [];
        }
        let found = false;
        offerList.forEach((id) => {
            if (id === offerId) {
                found = true;
            }
        });
        if (!found) {
            offerList.push(offerId);
        }
        this.storeOfferListRedeemed(offerList);
    }

    removeOffersListRedeemedOffer(offerId: number) {
        // add id to redeemed offer list
        let offerList: Array<number> = this.loadOffersListRedeemed();
        if (!offerList) offerList = [];
        const filteredList = offerList.filter(val => val !== offerId);
        this.storeOfferListRedeemed(filteredList);
    }

    // OFFER SEARCH
    storeUserLocation(location: LocationInfoRPC) {
        this.data.store(this.KEY_USER_LOCATION, location);
    }

    loadUserLocation(): LocationInfoRPC {
        let location = this.data.load(this.KEY_USER_LOCATION);
        if (!location) {
            location = {};
            this.storeUserLocation(location);
        }
        return _.cloneDeep(location);
    }

    // OFFER SEARCH RESULT LOCATION
    storeOfferSearchResultLocation(offerSearchResultLocation: OfferSearchResultLocation) {
        this.data.store(this.KEY_OFFER_SEARCH_RESULT_LOCATION, offerSearchResultLocation);
    }

    loadOfferSearchResultLocation(): OfferSearchResultLocation {
        let searchResultLocation = this.data.load(this.KEY_OFFER_SEARCH_RESULT_LOCATION);
        if (!searchResultLocation) {
            searchResultLocation = {};
        }
        return _.cloneDeep(searchResultLocation);
    }

    // OFFER SEARCH
    storeOfferSearch(offerSearch: OfferSearch) {
        this.data.store(this.KEY_OFFER_SEARCH, offerSearch);
    }

    loadOfferSearch(): OfferSearch {
        let search = this.data.load(this.KEY_OFFER_SEARCH);
        if (!search) {
            search = {
                location: true,
                zipCode: null,
                radius: 50
            };
            this.storeOfferSearch(search);
        }

        if (search.location)
          search.zipCode = null;

        return _.cloneDeep(search);
    }

    // OFFER SEARCH DATE
    storeOfferSearchDate(date: Date) {
        this.data.store(this.KEY_OFFER_SEARCH_DATE, toDateStr(date));
    }

    loadOfferSearchDate(): Date {
        return toDate(this.data.load(this.KEY_OFFER_SEARCH_DATE));
    }

    // OFFER SEARCH EXPIRES DATE
    storeOfferSearchExpiresDate(date: Date) {
        this.data.store(this.KEY_OFFER_SEARCH_EXPIRES_DATE, toDateStr(date));
    }

    loadOfferSearchExpiresDate(): Date {
        return toDate(this.data.load(this.KEY_OFFER_SEARCH_EXPIRES_DATE));
    }

    // SHARE DATE
    storeShareDate(date: Date) {
        this.data.store(this.KEY_SHARE_DATE, toDateStr(date));
    }

    loadShareDate(): Date {
        return toDate(this.data.load(this.KEY_SHARE_DATE));
    }

    // NOTIFICATION DATA
    storeNotifications(notified: {[key: string]: NotificationInfo }) {
        this.data.store(this.KEY_NOTIFICATIONS, notified);
    }

    loadNotifications(): {[key: string]: NotificationInfo } {
        const notifications = this.data.load(this.KEY_NOTIFICATIONS);
        return notifications ? _.cloneDeep(notifications) : {};
    }

    // NOTIFICATION TOPICS
    storeNotificationTopics(topics: {[key: string]: boolean }) {
        this.data.store(this.KEY_NOTIFICATION_TOPICS, topics);
    }

    loadNotificationsTopics(): {[key: string]: boolean } {
        const topics = this.data.load(this.KEY_NOTIFICATION_TOPICS);
        return topics ? _.cloneDeep(topics) : {};
    }

    // RPC QUEUE
    storeRPCQueue(queue: Array<RPCRequest<any>>): void {
        this.data.store(this.KEY_RPC_QUEUE, queue);
    }

    loadRPCQueue(): Array<RPCRequest<any>> {
        const queue: Array<RPCRequest<any>> = this.data.load(this.KEY_RPC_QUEUE);
        return queue ? _.cloneDeep(queue) : [];
    }

    // --------------------------------------------------

    private userDataSuffix(env: string) {
        if (!env) {
            env = "prod";
        }
        return env === "prod" ? "" : "_" + env.toUpperCase();
    }

    private updateOfferTracker(id: number, exists: boolean) {
        let allOffers: {[key: string]: boolean } = this.data.load(this.KEY_OFFER_LIST_ALL);
        if (!allOffers) allOffers = {};
        if (exists) {
            allOffers[String(id)] = true;
        } else {
            delete allOffers[String(id)];
        }
        this.data.store(this.KEY_OFFER_LIST_ALL, allOffers);
    }

    private presetData() {
        const preset: any = null; // PLACE DEBUG DATA CAPTURE HERE
        if (preset) {
            Log.out("INIT PRESET DATA!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!");
            Object.keys(preset).forEach((key) => {
                this.data.store(key, preset[key]);
            });
        }
    }
}
